import { lazy, Suspense, useState, type ComponentType } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { PublicPageBoundary } from '@/components/layout/ErrorBoundary';
import { Skeleton } from '@/components/ui/Misc';
import { useAppSelector } from '@/store/store';
import type { RbacRole } from '@/types';

// Public and auth pages are eager: they are the entry points and must paint fast.
import { ResumeCheckPage } from '@/pages/public/ResumeCheckPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Console pages are split out — most sessions touch only a few of them.
/**
 * A public page fetched when it is first opened, not with the first page.
 *
 * Every public page was in the one bundle, so a visitor landing on the CV checker from
 * a search result downloaded the interview room, the apply form, the mark-up screen
 * and the layout gallery before seeing anything — a slow first paint on a phone, which
 * is the visit that counts. The checker stays in the bundle; the rest follow on demand.
 *
 * A deploy replaces the hashed files, so a tab opened before it asks for a chunk that
 * is gone. That reloads the page once, onto the new build, instead of showing an error.
 */
type LazyPage = ComponentType & { preload: () => Promise<unknown> };

function lazyPage<T extends Record<string, unknown>>(load: () => Promise<T>, name: keyof T): LazyPage {
  let loaded: ComponentType | undefined;
  const fetchPage = async (): Promise<ComponentType> => {
    try {
      const module = await load();
      sessionStorage.removeItem('chunk-reload');
      loaded = module[name] as ComponentType;
      return loaded;
    } catch (error) {
      if (!sessionStorage.getItem('chunk-reload')) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw error;
    }
  };
  const Lazy = lazy(async () => ({ default: await fetchPage() }));
  /*
   * Once the code is here, the page renders straight away instead of through
   * React.lazy, which suspends on its first render even for a module that has
   * arrived. Chosen once per mount: switching from one to the other later would
   * remount the page and lose what was typed into it.
   */
  function Page() {
    const [Component] = useState<ComponentType>(() => loaded ?? Lazy);
    return <Component />;
  }
  return Object.assign(Page, { preload: fetchPage });
}

/*
 * Sign-in and the console's frame are split out too. They pulled the form library, the
 * date library and the console's menus into the first bundle, which every visitor to
 * the CV checker downloaded and parsed without ever seeing either.
 */
const LoginPage = lazyPage(() => import('@/pages/auth/LoginPage'), 'LoginPage');
const SignupPage = lazyPage(() => import('@/pages/auth/SignupPage'), 'SignupPage');
const AppShell = lazyPage(() => import('@/components/layout/AppShell'), 'AppShell');

const ApplyPage = lazyPage(() => import('@/pages/public/ApplyPage'), 'ApplyPage');
const BookInterviewPage = lazyPage(() => import('@/pages/public/BookInterviewPage'), 'BookInterviewPage');
const BookingSuccessPage = lazyPage(() => import('@/pages/public/BookInterviewPage'), 'BookingSuccessPage');
const InterviewRoomPage = lazyPage(() => import('@/pages/public/InterviewRoomPage'), 'InterviewRoomPage');
const ResumeResultPage = lazyPage(() => import('@/pages/public/ResumeResultPage'), 'ResumeResultPage');
const ResumeImprovePage = lazyPage(() => import('@/pages/public/ResumeImprovePage'), 'ResumeImprovePage');
const ResumeTemplatePage = lazyPage(() => import('@/pages/public/ResumeTemplatePage'), 'ResumeTemplatePage');
const ResumeBuilderPage = lazyPage(() => import('@/pages/public/ResumeBuilderPage'), 'ResumeBuilderPage');
const ResumeRestylePage = lazyPage(() => import('@/pages/public/ResumeRestylePage'), 'ResumeRestylePage');
const ResumeLayoutsPage = lazyPage(() => import('@/pages/public/ResumeLayoutsPage'), 'ResumeLayoutsPage');
const MyCvsPage = lazyPage(() => import('@/pages/public/MyCvsPage'), 'MyCvsPage');
const ResumeTemplatesPage = lazyPage(() => import('@/pages/public/ResumeGuidePages'), 'ResumeTemplatesPage');
const FresherFormatPage = lazyPage(() => import('@/pages/public/ResumeGuidePages'), 'FresherFormatPage');
const ResumeKaiseBanayePage = lazyPage(() => import('@/pages/public/ResumeGuidePages'), 'ResumeKaiseBanayePage');
const AtsFriendlyResumePage = lazyPage(() => import('@/pages/public/ResumeTopicGuides'), 'AtsFriendlyResumePage');
const BcomFresherResumePage = lazyPage(() => import('@/pages/public/ResumeTopicGuides'), 'BcomFresherResumePage');
const AboutPage = lazyPage(() => import('@/pages/public/LegalPages'), 'AboutPage');
const PrivacyPage = lazyPage(() => import('@/pages/public/LegalPages'), 'PrivacyPage');
const TermsPage = lazyPage(() => import('@/pages/public/LegalPages'), 'TermsPage');
const PricingPage = lazyPage(() => import('@/pages/public/LegalPages'), 'PricingPage');
const RefundPolicyPage = lazyPage(() => import('@/pages/public/LegalPages'), 'RefundPolicyPage');
const ShippingPolicyPage = lazyPage(() => import('@/pages/public/LegalPages'), 'ShippingPolicyPage');
const ContactPage = lazyPage(() => import('@/pages/public/LegalPages'), 'ContactPage');

/**
 * The pages built to HTML at deploy, by address, so the first render can wait for the
 * page's code instead of swapping the delivered HTML for a loading logo and back.
 */
const PRERENDERED: Record<string, LazyPage> = {
  '/resume-builder': ResumeBuilderPage,
  '/resume-check/restyle': ResumeRestylePage,
  '/resume-templates': ResumeTemplatesPage,
  '/resume-format-for-freshers': FresherFormatPage,
  '/resume-kaise-banaye': ResumeKaiseBanayePage,
  '/ats-friendly-resume': AtsFriendlyResumePage,
  '/resume-for-bcom-freshers': BcomFresherResumePage,
  '/about': AboutPage,
  '/privacy': PrivacyPage,
  '/terms': TermsPage,
  '/pricing': PricingPage,
  '/refund-policy': RefundPolicyPage,
  '/shipping-policy': ShippingPolicyPage,
  '/contact': ContactPage,
  '/my-cvs': MyCvsPage,
  '/resume-check/layouts': ResumeLayoutsPage,
  '/resume-check/result': ResumeResultPage,
  '/resume-check/improve': ResumeImprovePage,
};

/** The code for the page at this address, if it is a split one; nothing to wait for otherwise. */
export function preloadPage(pathname: string): Promise<unknown> | undefined {
  return PRERENDERED[pathname.replace(/\/+$/, '') || '/']?.preload();
}

/** What shows for the moment a public page is on its way: the same logo the prerendered HTML holds. */
function PublicFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas">
      <img src="/logo.png" alt="" width={56} height={47} className="opacity-85" />
    </div>
  );
}

const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const JobsPage = lazy(() => import('@/pages/jobs/JobsPage').then((m) => ({ default: m.JobsPage })));
const PostJobPage = lazy(() => import('@/pages/jobs/PostJobPage').then((m) => ({ default: m.PostJobPage })));
const JobDetailPage = lazy(() => import('@/pages/jobs/JobDetailPage').then((m) => ({ default: m.JobDetailPage })));
const DistributeJobPage = lazy(() =>
  import('@/pages/jobs/DistributeJobPage').then((m) => ({ default: m.DistributeJobPage }))
);
const InterviewProcessPage = lazy(() =>
  import('@/pages/interviews/InterviewProcessPage').then((m) => ({ default: m.InterviewProcessPage }))
);
const ApplicationsPage = lazy(() =>
  import('@/pages/candidates/ApplicationsPage').then((m) => ({ default: m.ApplicationsPage }))
);
const CandidatesPage = lazy(() =>
  import('@/pages/candidates/CandidatesPage').then((m) => ({ default: m.CandidatesPage }))
);
const CandidateProfilePage = lazy(() =>
  import('@/pages/candidates/CandidateProfilePage').then((m) => ({ default: m.CandidateProfilePage }))
);
const InvitationsPage = lazy(() =>
  import('@/pages/interviews/InvitationsPage').then((m) => ({ default: m.InvitationsPage }))
);
const InterviewReviewPage = lazy(() =>
  import('@/pages/interviews/InterviewReviewPage').then((m) => ({ default: m.InterviewReviewPage }))
);
const InterviewReportsPage = lazy(() =>
  import('@/pages/interviews/InterviewReportsPage').then((m) => ({ default: m.InterviewReportsPage }))
);
const SchedulePage = lazy(() => import('@/pages/schedule/SchedulePage').then((m) => ({ default: m.SchedulePage })));
const CalendarSettingsPage = lazy(() =>
  import('@/pages/settings/CalendarSettingsPage').then((m) => ({ default: m.CalendarSettingsPage }))
);
const EmailSettingsPage = lazy(() =>
  import('@/pages/settings/EmailSettingsPage').then((m) => ({ default: m.EmailSettingsPage }))
);
const PlatformSettingsPage = lazy(() =>
  import('@/pages/settings/PlatformSettingsPage').then((m) => ({ default: m.PlatformSettingsPage }))
);
const OAuthSettingsPage = lazy(() =>
  import('@/pages/settings/OAuthSettingsPage').then((m) => ({ default: m.OAuthSettingsPage }))
);
const CompanySettingsPage = lazy(() =>
  import('@/pages/settings/CompanySettingsPage').then((m) => ({ default: m.CompanySettingsPage }))
);
const ProfilePage = lazy(() => import('@/pages/settings/ProfilePage').then((m) => ({ default: m.ProfilePage })));

function BootScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center p-8">
      <Skeleton className="h-24 w-64" />
    </div>
  );
}

/** Blocks the console until a session is confirmed, remembering where the user was headed. */
function RequireAuth() {
  const { status, initialising } = useAppSelector((state) => state.auth);
  const location = useLocation();

  if (initialising) return <BootScreen />;
  if (status !== 'authenticated') {
    // The bare domain is where a search result or a shared link lands. Anyone not
    // signed in there is a job seeker far more often than a recruiter, so they get the
    // CV checker; a recruiter's own link to a console page still asks them to sign in.
    if (location.pathname === '/') return <Navigate to="/resume-check" replace />;
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <Outlet />;
}

function RequireRole({ allowed }: { allowed: RbacRole[] }) {
  const role = useAppSelector((state) => state.auth.role);
  // A missing role is a misconfiguration, not an attack — send them somewhere useful.
  if (!role || !allowed.includes(role)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Signed-in users have no business on the sign-in page. */
function RedirectIfAuthenticated({ children }: { children: React.ReactElement }) {
  const { status, initialising } = useAppSelector((state) => state.auth);
  if (initialising) return <BootScreen />;
  return status === 'authenticated' ? <Navigate to="/" replace /> : children;
}

export const routes = [
  {
    path: '/login',
    element: (
      <RedirectIfAuthenticated>
        <Suspense fallback={<BootScreen />}>
          <LoginPage />
        </Suspense>
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: '/signup',
    element: (
      <RedirectIfAuthenticated>
        <Suspense fallback={<BootScreen />}>
          <SignupPage />
        </Suspense>
      </RedirectIfAuthenticated>
    ),
  },

  // Candidate-facing, no session required. Each is wrapped on its own rather than
  // nested under one shared element, so a crash on the interview page cannot take the
  // apply page down with it.
  //
  // The boundary matters more here than in the console. A candidate who hits a render
  // error has no navigation to escape to and no account to come back to, so a blank
  // page ends the application — silently, and without the recruiter ever hearing why.
  { path: '/apply/:jobId', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ApplyPage /></Suspense></PublicPageBoundary> },
  { path: '/ai-interview/:token', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><InterviewRoomPage /></Suspense></PublicPageBoundary> },
  { path: '/book-interview/:token', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><BookInterviewPage /></Suspense></PublicPageBoundary> },
  { path: '/booking-success', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><BookingSuccessPage /></Suspense></PublicPageBoundary> },
  // Open to anyone, with no job and no account behind it: the one screen written for
  // the person being screened rather than the person screening.
  { path: '/resume-check', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeCheckPage /></Suspense></PublicPageBoundary> },
  { path: '/resume-check/result', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeResultPage /></Suspense></PublicPageBoundary> },
  { path: '/resume-check/improve', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeImprovePage /></Suspense></PublicPageBoundary> },
  { path: '/resume-check/template/:id', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeTemplatePage /></Suspense></PublicPageBoundary> },
  // The two other ways in: no CV yet, or a CV that only needs a new design.
  { path: '/resume-builder', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeBuilderPage /></Suspense></PublicPageBoundary> },
  { path: '/resume-check/restyle', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeRestylePage /></Suspense></PublicPageBoundary> },
  { path: '/resume-check/layouts', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeLayoutsPage /></Suspense></PublicPageBoundary> },
  { path: '/my-cvs', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><MyCvsPage /></Suspense></PublicPageBoundary> },
  // The guides: the searches people make before they reach for a tool, answered with
  // the tool one click away. And who runs the site, and what it does with a CV.
  { path: '/resume-templates', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeTemplatesPage /></Suspense></PublicPageBoundary> },
  { path: '/resume-format-for-freshers', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><FresherFormatPage /></Suspense></PublicPageBoundary> },
  { path: '/resume-kaise-banaye', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ResumeKaiseBanayePage /></Suspense></PublicPageBoundary> },
  { path: '/ats-friendly-resume', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><AtsFriendlyResumePage /></Suspense></PublicPageBoundary> },
  { path: '/resume-for-bcom-freshers', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><BcomFresherResumePage /></Suspense></PublicPageBoundary> },
  { path: '/about', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><AboutPage /></Suspense></PublicPageBoundary> },
  { path: '/privacy', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><PrivacyPage /></Suspense></PublicPageBoundary> },
  { path: '/terms', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><TermsPage /></Suspense></PublicPageBoundary> },
  { path: '/pricing', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><PricingPage /></Suspense></PublicPageBoundary> },
  { path: '/refund-policy', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><RefundPolicyPage /></Suspense></PublicPageBoundary> },
  { path: '/shipping-policy', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ShippingPolicyPage /></Suspense></PublicPageBoundary> },
  { path: '/contact', element: <PublicPageBoundary><Suspense fallback={<PublicFallback />}><ContactPage /></Suspense></PublicPageBoundary> },

  {
    element: <RequireAuth />,
    children: [
      {
        element: (
          <Suspense fallback={<BootScreen />}>
            <AppShell />
          </Suspense>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'jobs', element: <JobsPage /> },
          { path: 'post-job', element: <PostJobPage /> },
          { path: 'jobs/:jobId', element: <JobDetailPage /> },
          { path: 'jobs/:jobId/edit', element: <PostJobPage /> },
          { path: 'jobs/:jobId/post', element: <DistributeJobPage /> },
          { path: 'jobs/:jobId/interview-process', element: <InterviewProcessPage /> },
          { path: 'applications', element: <ApplicationsPage /> },
          { path: 'candidates', element: <CandidatesPage /> },
          { path: 'candidates/:candidateId', element: <CandidateProfilePage /> },
          { path: 'invitations', element: <InvitationsPage /> },
          { path: 'interview-reports', element: <InterviewReportsPage /> },
          { path: 'interview-reports/:sessionId', element: <InterviewReviewPage /> },
          { path: 'schedule', element: <SchedulePage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'settings/calendar', element: <CalendarSettingsPage /> },
          { path: 'settings/email', element: <EmailSettingsPage /> },
          { path: 'settings/platform', element: <PlatformSettingsPage /> },
          // The LinkedIn OAuth callback redirects here; it is the same page.
          { path: 'settings/linkedin', element: <PlatformSettingsPage /> },
          {
            element: <RequireRole allowed={['platform_admin', 'client_admin']} />,
            children: [{ path: 'settings/company', element: <CompanySettingsPage /> }],
          },
          {
            element: <RequireRole allowed={['platform_admin']} />,
            children: [{ path: 'settings/oauth', element: <OAuthSettingsPage /> }],
          },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
];
